import { CommonModule } from '@angular/common'
import { Component, Input, ViewChild } from '@angular/core'
import { ContentStackService } from '@app/core/_services/content-stack.service'
import { FmAvatarComponent } from '@app/shared/components/fm-avatar/fm-avatar.component'
import { FmIconComponent } from '@app/shared/components/fm-icon/fm-icon.component'
import { ProfilePreviewComponent } from '../../../user/components/profile-preview/profile-preview.component'
import { Tooltip, TooltipModule } from 'primeng/tooltip'
import type { CalendarDayAvailablePartner } from '../calendar-day/calendar-day.component'
import { ButtonModule } from 'primeng/button'
import { Popover, PopoverModule } from 'primeng/popover'

@Component({
    selector: 'fm-available-partner-tile',
    imports: [
        PopoverModule,
        TooltipModule,
        ButtonModule,
        CommonModule,
        FmAvatarComponent,
        FmIconComponent,
        ProfilePreviewComponent,
    ],
    templateUrl: './available-partner-tile.component.html',
    styleUrl: './available-partner-tile.component.scss',
})
export class AvailablePartnerTileComponent {
    @ViewChild(Tooltip) private tooltip!: Tooltip
    @ViewChild(Popover) private popover!: Popover
    @Input() partnerInfo: CalendarDayAvailablePartner
    @Input() validPartner: boolean
    @Input() sessionTime: number
    @Input() sessionDuration: number
    @Input() profilePhoto: string
    @Input() profileSlug: string
    @Input() isSaved: boolean = false
    @Input() isQuietMode: boolean = false
    @Input() hasDirectBookablePartner: boolean = false
    @Input() hasAvailableGroupMeeting: boolean = false
    @Input() isSelectable: boolean

    constructor(private contentStackService: ContentStackService) {}

    ngOnInit(): void {}

    public overlayVisible: boolean
    private lastPointerType: string | null = null
    public onClickSendTimeslotPartnersToDynamicPanel(): void {
        this.contentStackService.pushTimeslotPartnersToDynamicPanel(
            this.partnerInfo.sessionTime,
            this.partnerInfo.sessionDuration,
        )
    }

    public onClickSendTimeslotGroupMeetingsToDynamicPanel(event: Event): void {
        event?.stopPropagation()
        event?.preventDefault()
        this.contentStackService.pushTimeslotGroupMeetingsToDynamicPanel(
            this.partnerInfo.sessionTime,
            this.partnerInfo.sessionDuration,
        )
    }

    showTooltip(ev: TouchEvent) {
        this.tooltip.activate()
        ev.stopPropagation()
        ev.preventDefault()
    }

    onTouchStartAvatar(ev: TouchEvent): void {
        // NOTE: Since PrimeNG 21, pTooltip also opens on touchstart, which shows the
        // hover tooltip alongside the touch popover below. Our template listener is
        // attached before the tooltip's (added in its ngAfterViewInit), so this keeps
        // touch input from reaching the tooltip. Don't preventDefault here: it wouldn't
        // stop the tooltip's listener and would block scrolling from the avatar.
        ev.stopImmediatePropagation()
    }

    onPointerEnterAvatar(ev: PointerEvent): void {
        this.lastPointerType = ev.pointerType
    }

    onMouseEnterAvatar(ev: MouseEvent): void {
        // NOTE: Touch input also produces synthetic (compat) mouse events, which would open
        // the hover tooltip alongside the touch popover: Chrome fires mouseenter mid-press
        // on a long press, iOS Safari after touchend on a semi-long press. A real mouse
        // always fires pointerenter (pointerType 'mouse') before mouseenter, while
        // synthetic ones don't, so block mouseenter whenever the last pointer was touch.
        if (this.lastPointerType === 'touch') {
            ev.stopImmediatePropagation()
        }
    }

    onTouchAvatar(ev): void {
        // NOTE: Using touchend rather than touchstart to avoid
        // "touch slop" when an element's border is touched triggering
        // the pointer events to trigger on different elements resulting
        // in the overlay and popover to be dismissed immediately.
        // If we later decide we want it to show on touchstart, we can implement
        // a justOpened flag and set a timer to only handle dismissal after 100ms
        this.popover.toggle(ev)
        ev.stopPropagation()
        ev.preventDefault()
    }

    onClickOverlay(ev: PointerEvent) {
        // Ensure that the click does not bubble down and trigger selection of any time
        // slot or create the hover effect on mobile
        this.popover.hide()
        this.overlayVisible = false
        ev.preventDefault()
        ev.stopPropagation()
    }

    onPointerDownOverlay(ev: PointerEvent): void {
        // Prevent this bubbling down and creating the hover effect
        ev.preventDefault()
        ev.stopPropagation()
    }

    closePopover(ev: PointerEvent): void {
        // NOTE: Use hide() rather than toggle(ev) when closing. toggle() with an event
        // whose target isn't the avatar (overlay / close button) makes PrimeNG queue a
        // re-show on that new target, which in v21 leaves the popover stuck in an
        // "open but not rendered" state so the next avatar tap is swallowed.
        this.popover.hide()
        ev.stopPropagation()
        ev.preventDefault()
    }
}
